import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import StakeTable, { Column } from './GameEngine/StakeTable';
import { toast } from 'react-toastify';
import { Button, Badge, Modal, FormInput, FormTextarea, FormSelect, FormMultiSelect } from '../components/ui';
import { formatDateTime } from '../utils/dateUtils';

type User = {
  id: string;
  name: string;
  phone: string;
  email: string;
  password2: string;
  total_deposit_usdt: number;
  total_withdrawal_usdt: number;
  is_blocked: boolean;
  block_reason: string | null;
  blocked_until: string | null;
  is_collaborator: boolean;
  wallets?: Record<string, number>;
  parent_name: string | null;
  parent_role: string | null;
  affiliate_name?: string;
  affiliate_email?: string;
  manager_name?: string;
  manager_email?: string;
  profile?: any;
};

const UserAvatar: React.FC<{ src?: string; name: string }> = ({ src, name }) => {
  const [error, setError] = useState(false);
  
  if (!src || src.includes("avatar.png") || error) {
    return (
      <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 shrink-0 shadow-sm">
        <svg className="w-5 h-5 text-slate-500" fill="currentColor" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={name} 
      onError={() => setError(true)}
      className="w-10 h-10 rounded-full object-cover border border-slate-700 shadow-sm shrink-0" 
    />
  );
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // ── Filters & Sorting ──────────────────────────────────────
  type StatusFilter = 'all' | 'active' | 'blocked';
  type SortOption = 
    | 'date_desc' 
    | 'date_asc' 
    | 'name_asc' 
    | 'name_desc' 
    | 'uid_desc' 
    | 'uid_asc' 
    | 'balance_desc' 
    | 'balance_asc'
    | 'status_active'
    | 'status_blocked';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('date_desc');
  const [showFilters, setShowFilters] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({ uid: "", username: "", password: "" });

  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [blockData, setBlockData] = useState({ uid: "", username: "", reason: "", blockedUntil: "" });

  const [isCreditDebitModalOpen, setIsCreditDebitModalOpen] = useState(false);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [creditDebitData, setCreditDebitData] = useState({ uid: "", username: "", amount: "", type: "credit" as "credit" | "debit", currency: "inr" });

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await apiClient.get('/api/currencies');
        if (res.data?.success && Array.isArray(res.data.currencies)) {
          setCurrencies(res.data.currencies.map((c: any) => c.code.toLowerCase()));
        }
      } catch (err) {
        console.error('Error fetching currencies:', err);
      }
    };
    fetchCurrencies();
  }, []);

  const queryClient = useQueryClient();

  const currentRole = (
    localStorage.getItem("role") || ""
  ).toLowerCase();

  const fetchUsersAPI = async () => {
    const token = localStorage.getItem("token");
    let url = `/getUserData?role=${currentRole}`;
    if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

    const response = await apiClient.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return Array.isArray(response.data) ? response.data.reverse() : [];
  };

  const { data: users = [], isLoading: isUsersLoading } = useQuery({
    queryKey: ['users', currentRole, searchTerm],
    queryFn: fetchUsersAPI
  });

  const { data: authForms = [] } = useQuery({
    queryKey: ['authForms'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await apiClient.get('/admin/auth-forms', {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data?.status ? response.data.data : [];
      } catch (e) {
        console.error(e);
        return [];
      }
    }
  });

  const customFieldLabels = React.useMemo(() => {
    const labelsMap: Record<string, string> = {};
    authForms.forEach((form: any) => {
      const customFields = form.fields_config?.custom_fields || [];
      customFields.forEach((field: any) => {
        if (field.id && field.label) {
          labelsMap[field.id] = field.label;
          const rawId = field.id.replace(/^field_/, '');
          labelsMap[rawId] = field.label;
        }
      });
    });
    return labelsMap;
  }, [authForms]);



  const handleDeleteUser = async (id: string, name: string) => {
    try {
      await apiClient.delete(`/deleteUser`, {
        data: { id },
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSnackbar(`User ${name} deleted successfully`);
      setTimeout(() => setSnackbar(null), 3000); // Hide snackbar after 3 seconds
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleOpenEditModal = (user: any) => {
    setEditUserData({ uid: user.id, username: user.profile?.name || user.email || user.id, password: "" });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditUserData({ uid: "", username: "", password: "" });
  };

  const handleEditUser = async () => {
    try {
      await apiClient.put(`/editProfile`, {
        uid: editUserData.uid,
        username: editUserData.username,
        password: editUserData.password || undefined, // Only send password if entered
      });

      queryClient.invalidateQueries({ queryKey: ['users'] }); // Refresh data
      setSnackbar(`User ${editUserData.username} edited successfully`);
      setTimeout(() => setSnackbar(null), 3000);
      handleCloseEditModal();
    } catch (error) {
      console.error("Failed to edit user:", error);
    }
  };

  const handleOpenBlockModal = (user: any) => {
    setBlockData({ uid: user.id, username: user.profile?.name || user.email || user.id, reason: "", blockedUntil: "" });
    setIsBlockModalOpen(true);
  };

  const handleCloseBlockModal = () => {
    setIsBlockModalOpen(false);
  };

  const handleBlockUser = async () => {
    try {
      const token = localStorage.getItem("token");
      await apiClient.post(`/blockUser`, {
        uid: blockData.uid,
        reason: blockData.reason,
        blockedUntil: blockData.blockedUntil || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSnackbar(`User ${blockData.username} blocked successfully`);
      setTimeout(() => setSnackbar(null), 3000);
      handleCloseBlockModal();
    } catch (error) {
      console.error("Failed to block user:", error);
    }
  };

  const handleUnblockUser = async (uid: string, username: string) => {
    try {
      const token = localStorage.getItem("token");
      await apiClient.post(`/unblockUser`, { uid }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSnackbar(`User ${username} unblocked successfully`);
      setTimeout(() => setSnackbar(null), 3000);
    } catch (error) {
      console.error("Failed to unblock user:", error);
    }
  };

  const handleOpenCreditDebitModal = (user: any, type: "credit" | "debit") => {
    const defaultCurrency = currencies.length > 0 ? currencies[0] : "inr";
    setCreditDebitData({ uid: user.id, username: user.profile?.name || user.email || user.id, amount: "", type, currency: defaultCurrency });
    setIsCreditDebitModalOpen(true);
  };

  const handleCloseCreditDebitModal = () => {
    setIsCreditDebitModalOpen(false);
    const defaultCurrency = currencies.length > 0 ? currencies[0] : "inr";
    setCreditDebitData({ uid: "", username: "", amount: "", type: "credit", currency: defaultCurrency });
  };

  const handleCreditDebitUser = async () => {
    if (!creditDebitData.amount || parseFloat(creditDebitData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const endpoint = creditDebitData.type === "credit" ? "/creditUser" : "/debitUser";

      await apiClient.post(`${endpoint}`, {
        uid: creditDebitData.uid,
        amount: parseFloat(creditDebitData.amount),
        currency: creditDebitData.currency,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      queryClient.invalidateQueries({ queryKey: ['users'] });
      const action = creditDebitData.type === "credit" ? "credited" : "debited";
      setSnackbar(`User ${creditDebitData.username} ${action} with ${creditDebitData.amount} ${creditDebitData.currency.toUpperCase()}`);
      setTimeout(() => setSnackbar(null), 3000);
      handleCloseCreditDebitModal();
    } catch (error) {
      console.error(`Failed to ${creditDebitData.type} user:`, error);
      toast.error(`Failed to ${creditDebitData.type} user. Please try again.`);
    }
  };

  const filteredUsers = users.filter((user: any) => {
    // Search
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const name = user.profile?.name || '';
      const email = user.email || '';
      const matchesSearch =
        user.id.toString().includes(searchTerm) ||
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter === 'active' && user.is_blocked) return false;
    if (statusFilter === 'blocked' && !user.is_blocked) return false;

    return true;
  });

  const sortedUsers = React.useMemo(() => {
    const list = [...filteredUsers];
    switch (sortBy) {
      case 'date_desc':
        return list.sort((a, b) => {
          const dateA = new Date(a.created_at || a.created || 0).getTime();
          const dateB = new Date(b.created_at || b.created || 0).getTime();
          return dateB - dateA;
        });
      case 'date_asc':
        return list.sort((a, b) => {
          const dateA = new Date(a.created_at || a.created || 0).getTime();
          const dateB = new Date(b.created_at || b.created || 0).getTime();
          return dateA - dateB;
        });
      case 'name_asc':
        return list.sort((a, b) => {
          const nameA = (a.profile?.name || a.name || a.email || '').toLowerCase();
          const nameB = (b.profile?.name || b.name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'name_desc':
        return list.sort((a, b) => {
          const nameA = (a.profile?.name || a.name || a.email || '').toLowerCase();
          const nameB = (b.profile?.name || b.name || b.email || '').toLowerCase();
          return nameB.localeCompare(nameA);
        });
      case 'uid_desc':
        return list.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      case 'uid_asc':
        return list.sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
      case 'balance_desc':
        return list.sort((a, b) => {
          const getBal = (u: any) => {
            if (!u.wallets) return 0;
            return Object.values(u.wallets).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0);
          };
          return getBal(b) - getBal(a);
        });
      case 'balance_asc':
        return list.sort((a, b) => {
          const getBal = (u: any) => {
            if (!u.wallets) return 0;
            return Object.values(u.wallets).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0);
          };
          return getBal(a) - getBal(b);
        });
      case 'status_active':
        return list.sort((a, b) => (a.is_blocked === b.is_blocked ? 0 : a.is_blocked ? 1 : -1));
      case 'status_blocked':
        return list.sort((a, b) => (a.is_blocked === b.is_blocked ? 0 : a.is_blocked ? -1 : 1));
      default:
        return list;
    }
  }, [filteredUsers, sortBy]);

  const total = sortedUsers.length;
  const totalPages = Math.ceil(total / limit);
  const paginatedUsers = sortedUsers.slice((page - 1) * limit, page * limit);

  const exportToCSV = () => {
    const headers = ['S.No', 'Email', 'UID', 'Registration Date', 'Status', 'Profile Data', 'IP Address', 'Location'];
    const csvRows = [headers.join(',')];

    sortedUsers.forEach((user: any, index: any) => {
       const status = user.is_blocked ? 'Blocked' : 'Active';
       const profileString = JSON.stringify(user.profile || {});
       const location = [user.location_city, user.location_country].filter(Boolean).join(', ') || 'Unknown';
       const regDate = (user.created_at || user.created) ? formatDateTime(user.created_at || user.created) : 'N/A';
       const row = [
         index + 1,
         user.email,
         user.id,
         regDate,
         status,
         profileString,
         user.ip_address || 'N/A',
         location
       ];
      // Escape quotes and format row
      const formattedRow = row.map(val => {
        const strVal = String(val).replace(/"/g, '""');
        return `"${strVal}"`;
      });
      csvRows.push(formattedRow.join(','));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "users_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: Column[] = [
    { key: 'index', label: 'S.No', render: (_: any, __: any, i: any) => i + 1 },
    { 
      key: 'user_info', 
      label: 'User', 
      render: (_, user: any) => {
        const avatar = user.profile?.avatar;
        const name = user.profile?.name || "N/A";
        return (
          <div className="flex items-center gap-3">
            <UserAvatar src={avatar} name={name} />
            <div className="flex flex-col">
              <span className="font-semibold text-white text-sm">{name}</span>
              <span className="text-slate-400 text-xs font-mono">UID: {user.id}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'contact_info',
      label: 'Contact Details',
      render: (_, user: any) => {
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-slate-300 font-semibold">{user.email}</span>
            {user.phone && <span className="text-[11px] text-slate-400">📞 {user.phone}</span>}
          </div>
        );
      }
    },
    {
      key: 'signup_method',
      label: 'Signup Method',
      render: (_, user: any) => {
        const isGoogle = !!user.profile?.google_id;
        return (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap inline-block ${isGoogle ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400' : 'bg-slate-800 border border-slate-700 text-slate-300'}`}>
            {isGoogle ? 'Google Signup' : 'Email & Password'}
          </span>
        );
      }
    },
    {
      key: 'location_ip',
      label: 'Location / IP',
      render: (_, user: any) => {
        const location = [user.location_city, user.location_country].filter(Boolean).join(', ') || 'Unknown Location';
        const ip = user.ip_address || 'Unknown IP';
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-slate-300 font-semibold">{location}</span>
            <span className="text-[11px] text-slate-400 font-mono">{ip}</span>
          </div>
        );
      }
    },
    ...(!['affiliate', 'manager'].includes(currentRole) ? [{ 
      key: 'wallets', 
      label: 'Wallet Balances', 
      render: (val: any) => {
        if (!val || Object.keys(val).length === 0) return <span className="text-gray-500 text-xs">No balance</span>;
        return (
          <div className="flex flex-col gap-1 max-h-24 overflow-y-auto pr-1">
            {Object.entries(val).map(([currency, balance]: [string, any]) => (
              <span key={currency} className="text-xs font-semibold px-2 py-0.5 bg-slate-800 rounded-md border border-slate-700 whitespace-nowrap">
                <span className="uppercase text-slate-400 mr-1">{currency}:</span> 
                <span className="text-white">{Number(balance).toFixed(2)}</span>
              </span>
            ))}
          </div>
        );
      }
    }] : []),
    ...(currentRole !== 'affiliate' ? [
      { key: 'parent_name', label: 'Referred By', render: (val: any) => val ? (
        <span className="text-white text-xs font-semibold">{val}</span>
      ) : <span className="text-gray-500 text-xs">—</span> }
    ] : []),
    { key: 'is_blocked', label: 'Status', render: (val: boolean) => <Badge variant={val ? 'danger' : 'active'} dot>{val ? 'Blocked' : 'Active'}</Badge> },
    {
      key: 'profile',
      label: 'Registration Details',
      render: (val: any, user: any) => {
        const name = val?.name || "";
        const customFields = val?.custom_fields || {};
        const rawDate = user.created_at || user.created;
        const formattedDate = rawDate ? formatDateTime(rawDate) : null;

        return (
          <div className="flex flex-col gap-1 max-w-[280px] max-h-[150px] overflow-y-auto pr-1">
            {formattedDate && (
              <span className="text-[11px] bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 rounded px-1.5 py-0.5 whitespace-nowrap">
                <span className="text-indigo-400 font-semibold mr-1">Reg Date:</span>
                {formattedDate}
              </span>
            )}
            {name && (
              <span className="text-xs font-semibold text-slate-300">
                <span className="text-slate-500 uppercase mr-1 text-[10px]">Username:</span>
                {name}
              </span>
            )}
            {Object.entries(customFields).map(([key, value]: [string, any]) => {
              let displayLabel = customFieldLabels[key] || customFieldLabels['field_' + key] || key.replace('field_', '');
              if (displayLabel.trim().toLowerCase() === 'name') {
                displayLabel = 'Full Name';
              }
              return (
                <span key={key} className="text-[11px] bg-slate-800/60 border border-slate-700/50 rounded px-1.5 py-0.5 whitespace-pre-wrap break-all">
                  <span className="text-slate-400 font-semibold mr-1 capitalize">{displayLabel}:</span>
                  <span className="text-white">{String(value)}</span>
                </span>
              );
            })}
            {Object.keys(customFields).length === 0 && !name && !formattedDate && (
              <span className="text-slate-500 text-xs font-mono">{JSON.stringify(val)}</span>
            )}
          </div>
        );
      }
    },
    ...(currentRole !== 'affiliate' ? [{
      key: 'actions', label: 'Actions', render: (_: any, user: any) => (
        <div className="flex gap-2 items-center flex-wrap min-w-[300px]">
          <button onClick={() => handleOpenEditModal(user)} className="bg-sky-500 hover:bg-sky-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">Edit</button>
          <button onClick={() => navigate(`/users/profile/${user.id}`, { state: { user } })} className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">Details</button>
          {user.is_blocked ? (
            <button onClick={() => handleUnblockUser(user.id, user.profile?.name || user.email || user.id)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">Unblock</button>
          ) : (
            <button onClick={() => handleOpenBlockModal(user)} className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">Block</button>
          )}
          <button onClick={() => handleOpenCreditDebitModal(user, "credit")} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">Credit</button>
          <button onClick={() => handleOpenCreditDebitModal(user, "debit")} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors">Debit</button>
        </div>
      )
    }] : [])
  ];

  return (
    <div className='ml-0 lg:ml-[256px] p-4 sm:p-6 min-h-screen text-white bg-[#151929]'>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">
          Users
        </h1>
        <button
          onClick={() => setShowFilters(f => !f)}
          className="flex lg:hidden items-center gap-2 px-4 py-2 bg-[#1e2337] border border-slate-700 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:border-indigo-500 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Filters'}
        </button>
      </div>

      {snackbar && (
        <div className="mb-4 text-center text-white bg-green-500 py-2 px-4 rounded-lg shadow-md">
          {snackbar}
        </div>
      )}


      




      <div className="mb-6">
        <StakeTable
          title=""
          columns={columns}
          data={paginatedUsers}
          searchPlaceholder="Search users by name, email, or UID..."
          onSearch={(val) => { setSearchTerm(val); setPage(1); }}
          emptyMessage="No users found."
          hasSidebarMargin={false}
          isLoading={isUsersLoading}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {/* Status Filter */}
              <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 shadow-sm h-9">
                <span className="text-slate-400 font-medium whitespace-nowrap">Status:</span>
                <select
                  value={statusFilter}
                  onChange={(e: any) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="bg-transparent text-white font-medium focus:outline-none cursor-pointer w-[68px] sm:w-[78px] truncate"
                >
                  <option value="all" className="bg-[#151929] text-white">All</option>
                  <option value="active" className="bg-[#151929] text-white">Active</option>
                  <option value="blocked" className="bg-[#151929] text-white">Blocked</option>
                </select>
              </div>

              {/* Sort By */}
              <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 shadow-sm h-9">
                <svg className="w-3.5 h-3.5 text-indigo-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                <span className="text-slate-400 font-medium whitespace-nowrap">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e: any) => { setSortBy(e.target.value); setPage(1); }}
                  className="bg-transparent text-white font-medium focus:outline-none cursor-pointer w-[105px] sm:w-[120px] truncate"
                >
                  <option value="date_desc" className="bg-[#151929] text-white">Newest First</option>
                  <option value="date_asc" className="bg-[#151929] text-white">Oldest First</option>
                  <option value="name_asc" className="bg-[#151929] text-white">Name: A-Z</option>
                  <option value="name_desc" className="bg-[#151929] text-white">Name: Z-A</option>
                  <option value="uid_desc" className="bg-[#151929] text-white">UID: High-Low</option>
                  <option value="uid_asc" className="bg-[#151929] text-white">UID: Low-High</option>
                  <option value="balance_desc" className="bg-[#151929] text-white">Balance: High-Low</option>
                  <option value="balance_asc" className="bg-[#151929] text-white">Balance: Low-High</option>
                  <option value="status_active" className="bg-[#151929] text-white">Active First</option>
                  <option value="status_blocked" className="bg-[#151929] text-white">Blocked First</option>
                </select>
              </div>

              {/* Reset if active */}
              {(sortBy !== 'date_desc' || statusFilter !== 'all' || searchTerm) && (
                <button
                  onClick={() => {
                    setSortBy('date_desc');
                    setStatusFilter('all');
                    setSearchTerm('');
                    setPage(1);
                  }}
                  title="Reset Filters"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-rose-400 hover:text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 rounded-lg transition-all h-9"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reset
                </button>
              )}

              {/* Export Excel */}
              <button
                onClick={exportToCSV}
                className="w-full sm:w-auto justify-center bg-[#646ECD] hover:bg-[#525ab5] text-white text-sm font-medium py-2 px-3 rounded-md flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap h-9"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Excel
              </button>
            </div>
          }
          bottomActions={null}
        />
      </div>

      {/* Edit User Modal */}
      <Modal open={isEditModalOpen} onClose={handleCloseEditModal} title="Edit User Profile"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={handleCloseEditModal}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleEditUser}>Save Changes</Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormInput label="Username" value={editUserData.username} onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })} />
          <FormInput label="New Password (optional)" type="password" placeholder="Leave blank to keep current" value={editUserData.password} onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })} />
        </div>
      </Modal>

      {/* Block User Modal */}
      <Modal open={isBlockModalOpen} onClose={handleCloseBlockModal} title={`Block User: ${blockData.username}`}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={handleCloseBlockModal}>Cancel</Button>
            <Button variant="warning" size="sm" onClick={handleBlockUser}>Confirm Block</Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormTextarea label="Reason for Blocking" value={blockData.reason} onChange={(e) => setBlockData({ ...blockData, reason: e.target.value })} placeholder="Enter reason..." rows={3} />
          <FormInput label="Block Until (optional)" type="datetime-local" value={blockData.blockedUntil} onChange={(e) => setBlockData({ ...blockData, blockedUntil: e.target.value })} />
          <p className="text-xs text-slate-500">Leave 'Block Until' empty for permanent block.</p>
        </div>
      </Modal>

      {/* Credit/Debit User Modal */}
      <Modal open={isCreditDebitModalOpen} onClose={handleCloseCreditDebitModal}
        title={`${creditDebitData.type === 'credit' ? 'Credit' : 'Debit'} Balance — ${creditDebitData.username}`}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={handleCloseCreditDebitModal}>Cancel</Button>
            <Button variant={creditDebitData.type === 'credit' ? 'success' : 'danger'} size="sm" onClick={handleCreditDebitUser}>
              Confirm {creditDebitData.type === 'credit' ? 'Credit' : 'Debit'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormSelect
            label="Currency"
            value={creditDebitData.currency}
            onChange={(e: any) => setCreditDebitData({ ...creditDebitData, currency: e.target.value })}
            options={currencies.length > 0 ? currencies.map(c => ({ value: c, label: c.toUpperCase() })) : [{ value: "inr", label: "INR" }]}
          />
          <FormInput label={`Amount (${creditDebitData.currency.toUpperCase()})`} type="number" placeholder="Enter amount" value={creditDebitData.amount} onChange={(e) => setCreditDebitData({ ...creditDebitData, amount: e.target.value })} step="0.01" min="0" />
          <div className="flex items-center gap-2 bg-slate-950/50 border border-slate-700 rounded-md px-3 py-2">
            <span className="text-lg">{creditDebitData.type === 'credit' ? '✅' : '❌'}</span>
            <p className="text-sm text-slate-300">{creditDebitData.type === 'credit' ? 'ADD' : 'SUBTRACT'} <span className="font-bold text-white">{creditDebitData.amount || '0'}</span> {creditDebitData.currency.toUpperCase()} from user balance</p>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Users;